import type { ReportRow } from '@/api/keywordMetrics';
import { parseNumberish } from '@/api/keywordMetrics';
import type { NegativeKeywordRow } from '@/api/negativeKeywords';
import { normalizeKeywordTextForGroup } from '@/api/unifiedKeywords';
import {
  EMPTY_RAW_METRICS,
  rowInCampaignScope,
  type MetricPolarity,
  type RawMetricTotals,
  type WorkspaceMetricColumnDef,
} from '@/utils/workspaceAdProductMetrics';
import { deriveTargetingTypeMetrics } from '@/utils/workspaceTargetingTypeMetrics';

export type SearchTermReportRow = {
  campaignId?: string;
  campaignName?: string;
  adGroupId?: string;
  adGroupName?: string;
  searchTerm?: string;
  keywordId?: string;
  targeting?: string;
  matchType?: string;
  match_type?: string;
  adProduct?: string;
  impressions?: number;
  clicks?: number;
  cost?: number;
  purchases30d?: number;
  sales30d?: number;
  isNegated?: boolean;
};

export type ParsedSearchTermRowKey = {
  campaignId: string;
  adGroupId: string;
  searchTerm: string;
  targeting: string;
};

export function searchTermTableRowKey(
  campaignId: string,
  adGroupId: string,
  searchTerm: string,
  targeting = ''
): string {
  return `${campaignId}|${adGroupId}|${encodeURIComponent(searchTerm.trim().toLowerCase())}|${encodeURIComponent(targeting.trim().toLowerCase())}`;
}

export function parseSearchTermTableRowKey(key: string): ParsedSearchTermRowKey | null {
  const parts = String(key || '').split('|');
  if (parts.length === 4) {
    try {
      const campaignId = parts[0].trim();
      const adGroupId = parts[1].trim();
      const searchTerm = decodeURIComponent(parts[2]).trim();
      const targeting = decodeURIComponent(parts[3]).trim();
      if (!campaignId) return null;
      return { campaignId, adGroupId, searchTerm, targeting };
    } catch {
      return null;
    }
  }
  if (parts.length >= 3) {
    const campaignId = parts[0].trim();
    const adGroupId = parts[1].trim();
    const searchTerm = parts.slice(2).join('|').trim();
    if (!campaignId || !searchTerm) return null;
    return { campaignId, adGroupId, searchTerm, targeting: '' };
  }
  if (parts.length >= 2 && parts[0].trim()) {
    return {
      campaignId: parts[0].trim(),
      adGroupId: parts[1].trim(),
      searchTerm: '',
      targeting: '',
    };
  }
  return null;
}

export type SearchTermEnrichedRow = {
  rowKey: string;
  searchTerm: string;
  target: string;
  campaignId: string;
  campaignName: string;
  adGroupId: string;
  adGroupName: string;
  matchType: string;
  isNegated: boolean;
  tags: string;
  adProduct: string;
  metrics: ReturnType<typeof deriveTargetingTypeMetrics>;
};

export type SearchTermMetricColumnDef = {
  key: keyof ReturnType<typeof deriveTargetingTypeMetrics>;
  label: string;
  polarity: MetricPolarity;
  format: WorkspaceMetricColumnDef['format'];
};

export const SEARCH_TERM_METRIC_COLUMNS: SearchTermMetricColumnDef[] = [
  { key: 'cost', label: 'Spend', polarity: 'lower_is_better', format: 'currency' },
  { key: 'cpc', label: 'CPC', polarity: 'lower_is_better', format: 'currency' },
  { key: 'ctr', label: 'CTR', polarity: 'higher_is_better', format: 'percent' },
  { key: 'cr', label: 'CR%', polarity: 'higher_is_better', format: 'percent' },
  { key: 'purchases30d', label: 'Orders', polarity: 'higher_is_better', format: 'int' },
  { key: 'impressions', label: 'Impressions', polarity: 'higher_is_better', format: 'int' },
  { key: 'clicks', label: 'Clicks', polarity: 'higher_is_better', format: 'int' },
  { key: 'sales30d', label: 'Sales', polarity: 'higher_is_better', format: 'currency' },
  { key: 'acos', label: 'ACoS', polarity: 'lower_is_better', format: 'percent' },
  { key: 'roas', label: 'ROAS', polarity: 'higher_is_better', format: 'ratio' },
];

export type KeywordCatalogLookup = {
  textByKeywordId: Map<string, string>;
  matchTypeByKeywordId: Map<string, string>;
};

export function buildKeywordCatalogLookup(
  keywords: Array<{
    keyword_id?: string;
    keyword_text?: string;
    match_type?: string;
  }>
): KeywordCatalogLookup {
  const textByKeywordId = new Map<string, string>();
  const matchTypeByKeywordId = new Map<string, string>();
  for (const kw of keywords) {
    const id = kw.keyword_id != null ? String(kw.keyword_id).trim() : '';
    if (!id) continue;
    if (kw.keyword_text) textByKeywordId.set(id, String(kw.keyword_text));
    if (kw.match_type) matchTypeByKeywordId.set(id, String(kw.match_type).toUpperCase());
  }
  return { textByKeywordId, matchTypeByKeywordId };
}

function normalizeNegativeMatchType(matchType: string): 'exact' | 'phrase' | null {
  const normalized = matchType.toLowerCase().replace(/_/g, '');
  if (normalized.includes('phrase')) return 'phrase';
  if (normalized.includes('exact')) return 'exact';
  return null;
}

/** Exact equality or phrase (negative phrase contained as whole words in search term). */
export function isSearchTermNegated(
  searchTerm: string,
  negatives: NegativeKeywordRow[],
  campaignId: string,
  adGroupId: string
): boolean {
  const termNorm = normalizeKeywordTextForGroup(searchTerm);
  if (!termNorm) return false;

  for (const neg of negatives) {
    const state = String(neg.state || '').toUpperCase();
    if (state === 'ARCHIVED' || state === 'DELETED') continue;

    const negCampaign = neg.campaign_id != null ? String(neg.campaign_id) : '';
    const negAdGroup = neg.ad_group_id != null ? String(neg.ad_group_id) : '';
    if (negCampaign && negCampaign !== campaignId) continue;
    if (negAdGroup && negAdGroup !== adGroupId) continue;

    const negText = normalizeKeywordTextForGroup(neg.keyword_text);
    if (!negText) continue;

    const matchKind = normalizeNegativeMatchType(neg.match_type || '');
    if (matchKind === 'exact') {
      if (termNorm === negText) return true;
      continue;
    }
    if (matchKind === 'phrase') {
      const termTokens = termNorm.split(/\s+/);
      const phraseTokens = negText.split(/\s+/);
      if (phraseTokens.length === 0) continue;
      for (let i = 0; i <= termTokens.length - phraseTokens.length; i++) {
        let ok = true;
        for (let j = 0; j < phraseTokens.length; j++) {
          if (termTokens[i + j] !== phraseTokens[j]) {
            ok = false;
            break;
          }
        }
        if (ok) return true;
      }
    }
  }
  return false;
}

export type NegationLookup = {
  isNegated: (searchTerm: string, campaignId: string, adGroupId: string) => boolean;
};

/** Index negatives by campaign/ad group so each row checks a small candidate set. */
export function buildNegationLookup(negatives: NegativeKeywordRow[]): NegationLookup {
  const accountWide: NegativeKeywordRow[] = [];
  const byCampaign = new Map<string, NegativeKeywordRow[]>();
  const byAdGroup = new Map<string, NegativeKeywordRow[]>();

  for (const neg of negatives) {
    const state = String(neg.state || '').toUpperCase();
    if (state === 'ARCHIVED' || state === 'DELETED') continue;

    const campaignId = neg.campaign_id != null ? String(neg.campaign_id) : '';
    const adGroupId = neg.ad_group_id != null ? String(neg.ad_group_id) : '';

    if (!campaignId) {
      accountWide.push(neg);
      continue;
    }
    if (adGroupId) {
      const key = `${campaignId}|${adGroupId}`;
      const bucket = byAdGroup.get(key);
      if (bucket) bucket.push(neg);
      else byAdGroup.set(key, [neg]);
      continue;
    }
    const bucket = byCampaign.get(campaignId);
    if (bucket) bucket.push(neg);
    else byCampaign.set(campaignId, [neg]);
  }

  return {
    isNegated(searchTerm: string, campaignId: string, adGroupId: string) {
      const scoped: NegativeKeywordRow[] = [...accountWide];
      const campaignNegs = byCampaign.get(campaignId);
      if (campaignNegs) scoped.push(...campaignNegs);
      const adGroupNegs = byAdGroup.get(`${campaignId}|${adGroupId}`);
      if (adGroupNegs) scoped.push(...adGroupNegs);
      if (scoped.length === 0) return false;
      return isSearchTermNegated(searchTerm, scoped, campaignId, adGroupId);
    },
  };
}

function toRaw(row: SearchTermReportRow): RawMetricTotals {
  return {
    impressions: parseNumberish(row.impressions) ?? 0,
    clicks: parseNumberish(row.clicks) ?? 0,
    cost: parseNumberish(row.cost) ?? 0,
    purchases30d: parseNumberish(row.purchases30d) ?? 0,
    sales30d: parseNumberish(row.sales30d) ?? 0,
  };
}

export function enrichSearchTermRows(
  rows: SearchTermReportRow[],
  scopedCampaignLookupKeys: Set<string> | null,
  campaignNameById: Map<string, string>,
  adGroupNameById: Map<string, string>,
  keywordLookup: KeywordCatalogLookup,
  negatives: NegativeKeywordRow[]
): SearchTermEnrichedRow[] {
  const out: SearchTermEnrichedRow[] = [];
  const negationLookup = buildNegationLookup(negatives);

  for (const row of rows) {
    const campaignId = row.campaignId ? String(row.campaignId) : '';
    const adGroupId = row.adGroupId ? String(row.adGroupId) : '';
    const searchTerm = row.searchTerm ? String(row.searchTerm).trim() : '';
    const adProduct = row.adProduct ? String(row.adProduct) : 'sponsored_products';
    if (!campaignId || !searchTerm) continue;
    if (!rowInCampaignScope(adProduct, campaignId, scopedCampaignLookupKeys)) continue;

    const keywordId = row.keywordId ? String(row.keywordId) : '';
    const targetingText = row.targeting ? String(row.targeting).trim() : '';
    const target =
      targetingText ||
      (keywordId ? keywordLookup.textByKeywordId.get(keywordId) : undefined) ||
      '—';
    const fromApi = String(row.matchType || row.match_type || '').trim();
    const matchType =
      (fromApi ? fromApi.toUpperCase() : undefined) ||
      (keywordId ? keywordLookup.matchTypeByKeywordId.get(keywordId) : undefined) ||
      '—';
    const apiCampaignName = row.campaignName ? String(row.campaignName).trim() : '';
    const apiAdGroupName = row.adGroupName ? String(row.adGroupName).trim() : '';

    out.push({
      rowKey: searchTermTableRowKey(campaignId, adGroupId, searchTerm, targetingText),
      searchTerm,
      target,
      campaignId,
      campaignName: apiCampaignName || campaignNameById.get(campaignId) || campaignId,
      adGroupId,
      adGroupName: apiAdGroupName || adGroupNameById.get(adGroupId) || adGroupId || '—',
      matchType,
      isNegated:
        typeof row.isNegated === 'boolean'
          ? row.isNegated
          : negationLookup.isNegated(searchTerm, campaignId, adGroupId),
      tags: '',
      adProduct,
      metrics: deriveTargetingTypeMetrics(toRaw(row)),
    });
  }

  out.sort((a, b) => b.metrics.cost - a.metrics.cost);
  return out;
}

export function parseSearchTermRowsPayload(data: unknown): {
  rows: SearchTermReportRow[];
  totalCount: number;
  rowCount: number;
  limit: number | null;
  offset: number;
} {
  if (!data || typeof data !== 'object') {
    return { rows: [], totalCount: 0, rowCount: 0, limit: null, offset: 0 };
  }
  const raw = data as Record<string, unknown>;
  const rows = Array.isArray(raw.rows) ? (raw.rows as SearchTermReportRow[]) : [];
  const totalCount =
    typeof raw.totalCount === 'number'
      ? raw.totalCount
      : typeof raw.rowCount === 'number'
        ? raw.rowCount
        : rows.length;
  const rowCount = typeof raw.rowCount === 'number' ? raw.rowCount : rows.length;
  const limit = typeof raw.limit === 'number' ? raw.limit : null;
  const offset = typeof raw.offset === 'number' ? raw.offset : 0;
  return { rows, totalCount, rowCount, limit, offset };
}

export function emptySearchTermMetrics(): ReturnType<typeof deriveTargetingTypeMetrics> {
  return deriveTargetingTypeMetrics({ ...EMPTY_RAW_METRICS });
}

/** Flatten ReportRow-like keyword report fields when joining labels. */
export function reportRowKeywordText(row: ReportRow | undefined): string {
  return row?.keywordText ? String(row.keywordText) : '';
}
